import { useMemo } from "react";
import { useSubjects, useChapters } from "@/hooks/useCurriculum";
import type { ChapterRecord, SubjectRecord } from "@/lib/api-types";

type ChapterContext = {
  chapter: ChapterRecord;
  subject: SubjectRecord | null;
  className: string;
};

/**
 * Resolves chapter + subject + class metadata for a given chapter_id.
 *
 * The backend has no single `GET /curriculum/chapters/:id` endpoint, so we
 * reconstruct the context from the curriculum LIST endpoints that DO exist.
 * Ids follow a deterministic convention (see seed-curriculum.mjs):
 *
 *   chapter_id = "<subject_id>-ch<order>"   e.g. "class-11-physics-ch1"
 *   subject_id = "<class_id>-<slug>"        e.g. "class-11-physics"
 *   class_id   = "class-<grade>"            e.g. "class-11"
 *
 * From the chapter_id we derive subject_id (strip the `-ch<n>` suffix) and
 * class_id (the `class-<n>` prefix), then look the records up in the cached
 * `useChapters(subjectId)` + `useSubjects(classId)` lists. Both queries are
 * shared with the browse pages, so this is usually a cache hit (no extra
 * round-trip) and the breadcrumb resolves instantly.
 *
 * Degrades gracefully: returns `data: null` if the chapter can't be found,
 * which keeps `useChapterAccess` on its optimistic "unknown" path and lets
 * the backend signed-URL endpoint stay the source of truth.
 */
function deriveIds(chapterId: string | undefined): {
  subjectId: string | undefined;
  classId: string | undefined;
} {
  if (!chapterId) return { subjectId: undefined, classId: undefined };
  const subjectId = chapterId.replace(/-ch\d+$/i, "");
  const m = chapterId.match(/^(class-\d+)/i);
  return { subjectId, classId: m ? m[1] : undefined };
}

export function useChapterContext(chapterId: string | undefined) {
  const { subjectId, classId } = useMemo(() => deriveIds(chapterId), [chapterId]);

  const chaptersQ = useChapters(subjectId);
  const subjectsQ = useSubjects(classId);

  const isLoading = chaptersQ.isLoading || subjectsQ.isLoading;
  const isError = chaptersQ.isError;

  const data = useMemo<ChapterContext | null>(() => {
    if (!chapterId) return null;
    const chapter = chaptersQ.data?.find((c) => c.chapter_id === chapterId) ?? null;
    if (!chapter) return null;
    const subject = subjectsQ.data?.find((s) => s.subject_id === subjectId) ?? null;
    return { chapter, subject, className: classId ?? "" };
  }, [chapterId, chaptersQ.data, subjectsQ.data, subjectId, classId]);

  return { data, isLoading, isError };
}
